import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BeneficiaryInput } from '@monigo/shared';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/queryKeys.js';

export interface Beneficiary {
  id: string;
  full_name: string;
  country: string;
  payout_method: 'mobile_money' | 'bank';
  payout_details: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
}

export function useBeneficiaries() {
  return useQuery({
    queryKey: qk.beneficiaries,
    queryFn: () => callFunction<{ items: Beneficiary[] }>('beneficiaries-list'),
  });
}

export function useCreateBeneficiary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BeneficiaryInput) =>
      callFunction<{ beneficiary: Beneficiary }>('beneficiaries-create', { body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.beneficiaries }),
  });
}

export function useUpdateBeneficiary(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BeneficiaryInput) =>
      callFunction<{ beneficiary: Beneficiary }>('beneficiaries-update', {
        method: 'PATCH',
        body: input,
        query: { id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.beneficiaries }),
  });
}

export function useDeleteBeneficiary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      callFunction('beneficiaries-delete', { method: 'DELETE', query: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.beneficiaries }),
  });
}
